// The save container: a small binary envelope around JSON, compressed with the platform's CompressionStream (browsers
// and Node 18+), so a save costs no codec library. Binary fields (Uint8Array anywhere in the value, e.g. a physics
// engine's world snapshot) travel beside the JSON as raw bytes, not as base64 inside it: base64 grows them by a third
// and compresses worse.
//
// Layout: "RPGS" | u8 container version (1) | u8 compression (0 none, 1 gzip, 2 deflate-raw) | payload.
// The payload (compressed as a whole): u32 JSON length | JSON (UTF-8) | u32 blob count | (u32 length | bytes) per blob.
// Each Uint8Array in the value is replaced in the JSON by `{ "$rpgBin": index }`. Integers are little-endian.

const MAGIC = [0x52, 0x50, 0x47, 0x53] as const; // "RPGS"
const CONTAINER_VERSION = 1;
const BIN_KEY = "$rpgBin";

/** How the payload is compressed. `gzip` is the default; `deflate-raw` saves gzip's 18-byte header and trailer. */
export type Compression = "none" | "gzip" | "deflate-raw";
const CODES: Record<Compression, number> = { none: 0, gzip: 1, "deflate-raw": 2 };
const NAMES: Record<number, Compression> = { 0: "none", 1: "gzip", 2: "deflate-raw" };

/** What `encodeSave` measured: the JSON, the raw payload and the stored bytes. */
export interface EncodeStats {
  /** The JSON part, bytes. */
  jsonBytes: number;
  /** The binary fields, bytes. */
  binaryBytes: number;
  /** JSON plus binary plus framing, before compression. */
  rawBytes: number;
  /** What is stored. */
  storedBytes: number;
  compression: Compression;
}

/** A value the container can't hold. */
export class SaveCodecError extends Error {
  override name = "SaveCodecError";
}

const view = (b: Uint8Array): DataView => new DataView(b.buffer, b.byteOffset, b.byteLength);

/** Run bytes through a CompressionStream or DecompressionStream. */
async function pipe(bytes: Uint8Array, stream: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  const body = new Blob([new Uint8Array(bytes)]).stream().pipeThrough(stream as unknown as ReadableWritablePair<Uint8Array, Uint8Array>);
  return new Uint8Array(await new Response(body).arrayBuffer());
}

/** Is the platform's CompressionStream here (browsers since 2023, Node 18+)? */
export const canCompress = (): boolean => typeof CompressionStream === "function" && typeof DecompressionStream === "function";

/** The JSON text of `value` with every Uint8Array pulled out into `blobs`. */
function split(value: unknown): { json: string; blobs: Uint8Array[] } {
  const blobs: Uint8Array[] = [];
  const json = JSON.stringify(value, function (this: unknown, key, v: unknown) {
    // JSON.stringify calls toJSON first; a Uint8Array has none, so `v` is the array itself
    if (v instanceof Uint8Array) {
      blobs.push(v);
      return { [BIN_KEY]: blobs.length - 1 };
    }
    if (v && typeof v === "object" && !Array.isArray(v) && Object.prototype.hasOwnProperty.call(v, BIN_KEY)) {
      throw new SaveCodecError(`save value at "${key}" uses the reserved key ${BIN_KEY}`);
    }
    return v;
  });
  if (json === undefined) throw new SaveCodecError("save value is not JSON-serialisable");
  return { json, blobs };
}

/** Encode a JSON-like value (Uint8Array fields allowed) into the container. */
export async function encodeSave(value: unknown, o: { compression?: Compression } = {}): Promise<{ bytes: Uint8Array; stats: EncodeStats }> {
  let compression = o.compression ?? "gzip";
  if (compression !== "none" && !canCompress()) compression = "none";
  const { json, blobs } = split(value);
  const jsonBytes = new TextEncoder().encode(json);
  const binaryBytes = blobs.reduce((s, b) => s + b.byteLength, 0);
  const raw = new Uint8Array(4 + jsonBytes.byteLength + 4 + blobs.length * 4 + binaryBytes);
  const dv = view(raw);
  let at = 0;
  dv.setUint32(at, jsonBytes.byteLength, true);
  raw.set(jsonBytes, (at += 4));
  dv.setUint32((at += jsonBytes.byteLength), blobs.length, true);
  at += 4;
  for (const b of blobs) {
    dv.setUint32(at, b.byteLength, true);
    raw.set(b, (at += 4));
    at += b.byteLength;
  }
  const payload = compression === "none" ? raw : await pipe(raw, new CompressionStream(compression));
  const bytes = new Uint8Array(6 + payload.byteLength);
  bytes.set(MAGIC, 0);
  bytes[4] = CONTAINER_VERSION;
  bytes[5] = CODES[compression];
  bytes.set(payload, 6);
  return { bytes, stats: { jsonBytes: jsonBytes.byteLength, binaryBytes, rawBytes: raw.byteLength, storedBytes: bytes.byteLength, compression } };
}

/** Is this a save container (by its magic bytes)? */
export const isSaveContainer = (bytes: Uint8Array): boolean => bytes.byteLength >= 6 && MAGIC.every((m, i) => bytes[i] === m);

/** Decode a container back into its value (Uint8Array fields restored). Throws SaveCodecError on anything else. */
export async function decodeSave(bytes: Uint8Array): Promise<unknown> {
  if (!isSaveContainer(bytes)) throw new SaveCodecError("not a save container (bad magic)");
  if (bytes[4] !== CONTAINER_VERSION) throw new SaveCodecError(`save container version ${bytes[4]} is not ${CONTAINER_VERSION}`);
  const compression = NAMES[bytes[5]!];
  if (!compression) throw new SaveCodecError(`unknown save compression ${bytes[5]}`);
  const body = bytes.subarray(6);
  const raw = compression === "none" ? body : await pipe(body, new DecompressionStream(compression));
  const dv = view(raw);
  let at = 0;
  const jsonLen = dv.getUint32(at, true);
  const json = new TextDecoder().decode(raw.subarray((at += 4), at + jsonLen));
  at += jsonLen;
  const count = dv.getUint32(at, true);
  at += 4;
  const blobs: Uint8Array[] = [];
  for (let i = 0; i < count; i++) {
    const n = dv.getUint32(at, true);
    at += 4;
    if (at + n > raw.byteLength) throw new SaveCodecError("save container is truncated");
    blobs.push(raw.slice(at, at + n));
    at += n;
  }
  return JSON.parse(json, (_k, v: unknown) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const keys = Object.keys(v);
      if (keys.length === 1 && keys[0] === BIN_KEY) {
        const blob = blobs[(v as Record<string, number>)[BIN_KEY]!];
        if (!blob) throw new SaveCodecError("save container refers to a missing blob");
        return blob;
      }
    }
    return v;
  });
}

/** Encode a small JSON value as UTF-8 bytes (no container): for bookkeeping records such as sync state. */
export const jsonBytes = (value: unknown): Uint8Array => new TextEncoder().encode(JSON.stringify(value));
/** The inverse of {@link jsonBytes}. */
export const fromJsonBytes = <T>(bytes: Uint8Array): T => JSON.parse(new TextDecoder().decode(bytes)) as T;
